// Copyright © 2024 Ory Corp
// SPDX-License-Identifier: Apache-2.0
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { FetchError, RequiredError, ResponseError } from "../";
import { isBrowserLocationChangeRequired, isCsrfError, isNeedsPrivilegedSessionError, isResponseError, isSelfServiceFlowExpiredError, } from "./error";
/**
 * Use this as the catch handler for all flow-related SDK calls, such as creating a login or submitting a login.
 *
 *
 * @param opts - The configuration object.
 */
export const handleFlowError = (opts) => (err) => __awaiter(void 0, void 0, void 0, function* () {
    if (isResponseError(err)) {
        switch (err.response.status) {
            case 404: // Does not exist
                opts.onRestartFlow();
                return;
            case 410: // Expired
                const body = yield toBody(err.response);
                if (isSelfServiceFlowExpiredError(body)) {
                    opts.onRestartFlow(body.use_flow_id);
                    return;
                }
                // Re-initialize the flow
                opts.onRestartFlow();
                return;
            case 400:
                return opts.onValidationError(
                // TODO: maybe we can do actual type checking here?
                (yield err.response.json()));
            case 403: // This typically happens with CSRF violations.
            case 422: {
                const body = yield toBody(err.response);
                if (isBrowserLocationChangeRequired(body) &&
                    body.redirect_browser_to) {
                    opts.onRedirect(body.redirect_browser_to, true);
                    return;
                }
                else if (isSelfServiceFlowExpiredError(body)) {
                    opts.onRestartFlow(body.use_flow_id);
                    return;
                }
                else if (isNeedsPrivilegedSessionError(body)) {
                    opts.onRedirect(body.redirect_browser_to, true);
                    return;
                }
                else if (isCsrfError(body)) {
                    opts.onRestartFlow();
                    return;
                }
                throw new ResponseError(err.response, "The Ory API endpoint returned a response code the SDK does not know how to handle. Please check the network tab for more information:" +
                    JSON.stringify(body));
            }
            default:
                throw new ResponseError(err.response, "The Ory API endpoint returned a response code the SDK does not know how to handle. Please check the network tab for more information.");
        }
    }
    else if (err instanceof FetchError) {
        throw new FetchError(err, "Unable to call the API endpoint. Ensure that CORS is set up correctly and that you have provided a valid SDK URL to Ory Elements.");
    }
    else if (err instanceof RequiredError) {
        // TODO (@aeneasr) Happens on submit usually. Not sure how to handle yet.
    }
    throw err;
});
export function toBody(response) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return (yield response.clone().json());
        }
        catch (e) {
            throw new ResponseError(response, "The Ory API endpoint returned a response the SDK does not know how to handle:" +
                (yield response.text()));
        }
    });
}
